import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Handshake, Users, Award, TrendingUp, Shield, Zap,
  User, Building2, Phone, Mail, MapPin, MessageSquare,
  CheckCircle2, ArrowRight, ExternalLink, Loader2,
  HardHat, HomeIcon, Briefcase, Ruler, KeyRound,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DynamicIsland from '../components/DynamicIsland';
import Footer from '../components/Footer';
import LeadFormPopup from '../components/LeadFormPopup';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { provinces } from '../mockData';
import { getLeadTrackingFields } from '../utils/leadTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const professionalTypes = [
  'Geometra',
  'Architetto',
  'Ingegnere edile',
  'Amministratore di condominio',
  'Agente immobiliare',
  'Impresa edile',
  'Consulente/Studio tecnico',
  'Altro',
];

const targetAudience = [
  { icon: Ruler, label: 'Geometri' },
  { icon: HomeIcon, label: 'Architetti' },
  { icon: Briefcase, label: 'Amm. Condominio' },
  { icon: KeyRound, label: 'Ag. Immobiliari' },
  { icon: HardHat, label: 'Imprese edili' },
];

const benefits = [
  { icon: TrendingUp, title: 'Provvigioni competitive', desc: 'Riconoscimento economico su ogni intervento andato a buon fine, con condizioni trasparenti.' },
  { icon: Shield, title: 'Zero rischio operativo', desc: 'Nessun costo di ingresso, nessun impegno di volume. Tu presenti, noi eseguiamo con la nostra assicurazione.' },
  { icon: Award, title: 'Marchi certificati', desc: 'RevoUltra® con garanzia fino a 25 anni. Certificazioni CE, ETA, BBA. Documentazione tecnica sempre disponibile.' },
  { icon: Zap, title: 'Sopralluogo in 48h', desc: 'I tuoi clienti ricevono un tecnico specializzato entro 2 giorni lavorativi con preventivo dettagliato.' },
  { icon: Users, title: 'Referente dedicato', desc: 'Un account manager segue te e i tuoi clienti dall\u2019inizio alla fine del progetto.' },
  { icon: Handshake, title: 'Materiale marketing', desc: 'Brochure, presentazioni, esempi di case study e ROI da mostrare ai tuoi clienti.' },
];

const brands = [
  {
    name: 'Evoluzione Tetto',
    tag: 'Manutenzione e rifacimento tetti',
    desc: 'Il marchio principale: manutenzione, ripasso, rifacimento coperture e smaltimento eternit senza ponteggi. Garanzia fino a 10 anni.',
    href: 'https://evoluzionetetto.com',
    logoType: 'image',
    logoSrc: 'https://customer-assets.emergentagent.com/job_smart-roofing-2/artifacts/7ngvxkwz_2.png',
    color: '#FF5722',
  },
  {
    name: 'RevoUltra',
    tag: 'Impermeabilizzazione nanopolimerica',
    desc: 'Sistema brevettato di impermeabilizzazione a base nanopolimerica applicabile su qualsiasi copertura. Garanzia scritta fino a 25 anni. Certificazioni ETA, BBA, CE.',
    href: 'https://revoultra.it',
    logoType: 'text-revoultra',
    color: '#15803d',
  },
  {
    name: 'Isoliamo',
    tag: 'Isolamento tetti e intercapedini',
    desc: 'Isolamento termico di tetti e intercapedini murarie con materiali certificati. Riduzione consumi energetici fino al 40%. Ideale per ristrutturazioni ed ecobonus.',
    href: 'https://isoliamo.com',
    logoType: 'image',
    logoSrc: 'https://customer-assets.emergentagent.com/job_b2eca1db-26c1-43d9-9c75-7df69e4d27a2/artifacts/3qmu0ew3_5.png',
    color: '#1e40af',
  },
  {
    name: 'RevoQuota',
    tag: 'Lavori in quota certificati',
    desc: 'Specialisti nel lavoro in quota con sistemi di sicurezza anticaduta certificati D.Lgs. 81/2008. Nessun ponteggio: risparmio fino al 70% sui costi.',
    href: 'https://revoquota.com',
    logoType: 'image',
    logoSrc: 'https://customer-assets.emergentagent.com/job_b2eca1db-26c1-43d9-9c75-7df69e4d27a2/artifacts/r9hxdhmu_2BF8C848-2593-4E9F-9AAB-F6A193DD2537.jpeg',
    color: '#0f172a',
  },
];

const RevoUltraLogo = () => (
  <div className="flex items-baseline gap-1">
    <span className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit' }}>Revo</span>
    <span className="text-3xl font-bold tracking-tight text-[#15803d]" style={{ fontFamily: 'Outfit' }}>Ultra</span>
    <span className="text-sm text-[#15803d] font-semibold">®</span>
  </div>
);

const CollaborazioniPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', profession: '', company: '', phone: '', email: '', province: '', message: '', website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const update = (f, v) => setForm(prev => ({ ...prev, [f]: v }));

  const canSubmit = form.name && form.profession && form.phone && form.email && form.province;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);

    const desc = `Richiesta collaborazione B2B | Professione: ${form.profession}${form.company ? ' | Azienda: ' + form.company : ''}${form.message ? ' | Note: ' + form.message : ''}`;

    try {
      await axios.post(`${BACKEND_URL}/api/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        province: form.province,
        houseType: 'Collaborazione B2B',
        ownershipStatus: form.profession,
        problemDescription: desc,
        profession: form.profession,
        company: form.company,
        tipoIntervento: 'Candidatura Partner B2B',
        website: form.website,
        ...getLeadTrackingFields(),
      });

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-17960264240/HNy_CMqrr_obELDEj_RC',
          value: 1.0, currency: 'EUR',
        });
      }
      if (typeof window.fbq === 'function') window.fbq('track', 'Lead');

      toast.success('Richiesta inviata! Ti contatteremo entro 48 ore.');
      setSubmitted(true);
    } catch {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <DynamicIsland onOpenForm={() => setIsFormOpen(true)} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-slate-50 border-b border-slate-200 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,87,34,0.05),transparent_50%)]" />
        <div className="max-w-5xl mx-auto px-6 md:px-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1.5 mb-5">
              <Handshake className="w-3.5 h-3.5 text-[#FF5722]" />
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Programma Collaborazioni B2B</span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-5"
              style={{ fontFamily: 'Outfit' }}
              data-testid="collaborazioni-title"
            >
              Diventa <span className="text-[#FF5722]">partner</span><br />del Gruppo RevoGreen
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mb-8">
              Un programma dedicato a <strong className="text-slate-900">geometri, architetti, amministratori di condominio, agenti immobiliari</strong> e altri professionisti del settore edile e immobiliare che vogliono offrire ai propri clienti i servizi e i prodotti dei marchi del nostro gruppo.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {targetAudience.map((t) => (
                <div key={t.label} className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-full text-sm text-slate-700">
                  <t.icon className="w-3.5 h-3.5 text-[#FF5722]" />
                  {t.label}
                </div>
              ))}
            </div>

            <a
              href="#form-collaborazione"
              data-testid="collaborazioni-cta-hero"
              className="inline-flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-white font-bold px-7 py-3.5 rounded-full transition-all"
            >
              Candidati come partner
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#FF5722] text-sm font-semibold uppercase tracking-wider">Vantaggi per te</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-3" style={{ fontFamily: 'Outfit' }}>
              Perché collaborare con noi
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Un modello di partnership pensato per generare valore reale sia per te che per i tuoi clienti.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#FF5722]/30 hover:shadow-md transition-all"
                data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-11 h-11 bg-[#FF5722]/10 rounded-xl flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5 text-[#FF5722]" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{b.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands of the group */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#FF5722] text-sm font-semibold uppercase tracking-wider">L&apos;ecosistema RevoGreen</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-3" style={{ fontFamily: 'Outfit' }}>
              I marchi che offriamo ai tuoi clienti
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Un ecosistema integrato di soluzioni specializzate. Puoi proporre l&apos;intero pacchetto o singoli servizi in base alle esigenze del tuo cliente.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {brands.map((b, i) => (
              <motion.a
                key={b.name}
                href={b.href}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="group bg-white border border-slate-200 rounded-2xl p-6 md:p-8 hover:shadow-lg hover:border-slate-300 transition-all flex flex-col"
                data-testid={`brand-card-${b.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="h-16 flex items-center min-w-[120px]">
                    {b.logoType === 'text-revoultra' ? (
                      <RevoUltraLogo />
                    ) : (
                      <img src={b.logoSrc} alt={`${b.name} logo`} className="max-h-14 w-auto object-contain" />
                    )}
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#FF5722] transition-colors shrink-0 mt-1" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>{b.name}</h3>
                <p className="text-sm font-medium mb-3" style={{ color: b.color }}>{b.tag}</p>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">{b.desc}</p>
                <div className="mt-4 pt-4 border-t border-slate-100 inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 group-hover:text-[#FF5722] transition-colors">
                  Visita il sito
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#FF5722] text-sm font-semibold uppercase tracking-wider">Come funziona</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3" style={{ fontFamily: 'Outfit' }}>
              4 passi, zero complicazioni
            </h2>
          </div>

          <div className="space-y-4">
            {[
              { n: '01', t: 'Ti candidi', d: 'Compili il form qui sotto con i tuoi dati e la tua professione.' },
              { n: '02', t: 'Ti contattiamo entro 48 ore', d: 'Il nostro referente ti chiama per un colloquio conoscitivo e ti illustra il modello di collaborazione.' },
              { n: '03', t: 'Ricevi il kit partner', d: 'Contratto di partnership, materiale marketing, brochure tecniche e accesso al portale referral.' },
              { n: '04', t: 'Presenti i tuoi clienti', d: 'Ci segnali i clienti interessati, noi ci occupiamo di tutto (sopralluogo, preventivo, esecuzione). Tu incassi la provvigione.' },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-5 bg-slate-50 border border-slate-200 rounded-2xl p-5"
              >
                <div className="text-2xl font-bold text-[#FF5722] shrink-0" style={{ fontFamily: 'Outfit' }}>{s.n}</div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>{s.t}</h4>
                  <p className="text-sm text-slate-600">{s.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="form-collaborazione" className="py-20 bg-slate-50">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          {!submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm"
            >
              <div className="mb-6">
                <span className="text-[#FF5722] text-sm font-semibold uppercase tracking-wider">Candidatura Partner</span>
                <h2 className="text-2xl font-bold text-slate-900 mt-2 mb-2" style={{ fontFamily: 'Outfit' }}>
                  Diventa partner in 2 minuti
                </h2>
                <p className="text-sm text-slate-500">
                  Compila il form. Ti ricontatteremo entro 48 ore per fissare un colloquio conoscitivo.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="collaborazioni-form">
                <input type="text" name="website" value={form.website} onChange={(e) => update('website', e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text" required placeholder="Nome e Cognome *"
                      value={form.name} onChange={(e) => update('name', e.target.value)}
                      className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                      data-testid="collab-input-name"
                    />
                  </div>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text" placeholder="Studio / Azienda"
                      value={form.company} onChange={(e) => update('company', e.target.value)}
                      className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                      data-testid="collab-input-company"
                    />
                  </div>
                </div>

                <div>
                  <Select value={form.profession} onValueChange={(v) => update('profession', v)}>
                    <SelectTrigger className="h-12 bg-white border-slate-200 text-slate-900 rounded-xl" data-testid="collab-input-profession">
                      <SelectValue placeholder="Professione *" />
                    </SelectTrigger>
                    <SelectContent>
                      {professionalTypes.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="tel" required placeholder="Telefono *"
                      value={form.phone} onChange={(e) => update('phone', e.target.value)}
                      className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                      data-testid="collab-input-phone"
                    />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email" required placeholder="Email *"
                      value={form.email} onChange={(e) => update('email', e.target.value)}
                      className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                      data-testid="collab-input-email"
                    />
                  </div>
                </div>

                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                  <Select value={form.province} onValueChange={(v) => update('province', v)}>
                    <SelectTrigger className="pl-10 h-12 bg-white border-slate-200 text-slate-900 rounded-xl" data-testid="collab-input-province">
                      <SelectValue placeholder="Provincia *" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {provinces.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <Textarea
                    placeholder="Raccontaci brevemente la tua attività o le tue esigenze (opzionale)"
                    value={form.message} onChange={(e) => update('message', e.target.value)}
                    rows={4}
                    className="pl-10 pt-3 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl resize-none"
                    data-testid="collab-input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  data-testid="collab-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-6 py-4 rounded-xl transition-all mt-2"
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Invio in corso...</>
                  ) : (
                    <>Invia candidatura <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>

                <p className="text-xs text-slate-400 text-center pt-1">
                  Inviando la candidatura accetti la nostra{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#FF5722] hover:text-[#FF7043] underline">Privacy Policy</a>.
                </p>
              </form>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="bg-white border border-emerald-200 rounded-2xl p-8 md:p-12 text-center shadow-sm"
              data-testid="collab-success"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>
                Candidatura ricevuta!
              </h2>
              <p className="text-slate-600 mb-2">
                Grazie <strong className="text-slate-900">{form.name.split(' ')[0]}</strong>, abbiamo ricevuto la tua richiesta.
              </p>
              <p className="text-sm text-slate-500">
                Un nostro referente ti contatterà entro <strong className="text-slate-700">48 ore lavorative</strong> al numero <strong className="text-slate-700">{form.phone}</strong> per fissare un colloquio conoscitivo.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>
            Hai domande sulla collaborazione?
          </h2>
          <p className="text-slate-300 mb-6">
            Chiamaci direttamente: uno dei nostri account manager risponderà a tutti i tuoi dubbi.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="tel:800864621" className="inline-flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-white font-bold px-7 py-3 rounded-full transition-all">
              <Phone className="w-4 h-4" /> 800 86 46 21
            </a>
            <a href="mailto:info@revogreenitalia.com" className="inline-flex items-center gap-2 text-white border border-white/20 hover:border-white/40 px-7 py-3 rounded-full transition-all">
              <Mail className="w-4 h-4" /> info@revogreenitalia.com
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <LeadFormPopup isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </div>
  );
};

export default CollaborazioniPage;
